/**
 * Screen-space outlines for handle glyphs.
 *
 * AutoCAD gives each kind of dynamic block grip its own silhouette, which is how a
 * user tells at a glance what a grip does. We follow the same conventions:
 *   - flip      → double-headed arrow (the two states it swaps between)
 *   - list      → downward triangle (opens a list of states/values)
 *   - point     → square (a draggable position)
 *   - angle     → filled sector, the same wedge the angle dial draws
 * Everything else stays a circle, like the generic transform handles.
 *
 * Pure geometry, no rendering: the caller feeds the points to a closed, filled
 * polyline overlay. Coordinates are in screen pixels so glyphs keep a constant size
 * whatever the zoom.
 */
import type { Point2D } from "./types";

/** Silhouettes a handle can request through its draw options. */
export type HandleShape =
  | "circle"
  | "doubleArrow"
  | "triangleDown"
  | "square"
  | "angleSector";

/**
 * Outline of a handle glyph, centered on `center` and sized from `radius`
 * (the same radius the circle glyph would use).
 *
 * @returns the polygon points, or null for "circle" — which the caller draws as a
 *          point overlay rather than a polygon.
 */
export function buildHandleShapePoints(
  shape: HandleShape | undefined,
  center: Point2D,
  radius: number,
): Point2D[] | null {
  switch (shape) {
    case "doubleArrow":
      return buildDoubleArrow(center, radius);
    case "triangleDown":
      return buildTriangleDown(center, radius);
    case "square":
      return buildSquare(center, radius);
    case "angleSector":
      return buildAngleSector(center, radius);
    default:
      return null;
  }
}

/**
 * A double-headed arrow: a shaft with a head at each end, drawn clockwise from the
 * left tip. Two heads rather than one because a flip swaps between two states —
 * a single head would suggest a direction the value is heading towards.
 */
function buildDoubleArrow(center: Point2D, radius: number): Point2D[] {
  const { x, y } = center;
  // Shaft half-height, head half-height, and where each head meets the shaft.
  const shaft = radius * 0.28;
  const head = radius * 0.78;
  const neck = radius * 0.42;

  return [
    { x: x - radius, y },
    { x: x - neck, y: y - head },
    { x: x - neck, y: y - shaft },
    { x: x + neck, y: y - shaft },
    { x: x + neck, y: y - head },
    { x: x + radius, y },
    { x: x + neck, y: y + head },
    { x: x + neck, y: y + shaft },
    { x: x - neck, y: y + shaft },
    { x: x - neck, y: y + head },
  ];
}

/** Equilateral-ish triangle pointing down — AutoCAD's visibility/lookup grip. */
function buildTriangleDown(center: Point2D, radius: number): Point2D[] {
  const { x, y } = center;
  const half = radius * 0.95;
  const top = radius * 0.72;

  return [
    { x: x - half, y: y - top },
    { x: x + half, y: y - top },
    { x, y: y + radius },
  ];
}

/** Axis-aligned square — AutoCAD's point/linear parameter grip. */
function buildSquare(center: Point2D, radius: number): Point2D[] {
  const { x, y } = center;
  const half = radius * 0.8;

  return [
    { x: x - half, y: y - half },
    { x: x + half, y: y - half },
    { x: x + half, y: y + half },
    { x: x - half, y: y + half },
  ];
}

/**
 * A filled circular sector: two straight edges meeting at a vertex, closed by an arc.
 * It is the wedge the angle dial fills in, shrunk to grip size, so the grip and the
 * dial it opens read as the same thing.
 */
function buildAngleSector(center: Point2D, radius: number): Point2D[] {
  // Vertex pushed down-left so the sector sits centred in the glyph's box.
  const vertexX = center.x - radius * 0.55;
  const vertexY = center.y + radius * 0.55;
  const arm = radius * 1.5;
  const sweepDegrees = 75;
  const steps = 6;

  const points: Point2D[] = [{ x: vertexX, y: vertexY }];
  for (let step = 0; step <= steps; step++) {
    const angle = ((sweepDegrees * step) / steps) * (Math.PI / 180);
    points.push({
      x: vertexX + arm * Math.cos(angle),
      y: vertexY - arm * Math.sin(angle),
    });
  }
  return points;
}

/** Line icons drawn in white inside a handle disc. */
export type HandleIcon = "move";

/**
 * Path of a handle's inner icon, centered on `center` and sized from the handle
 * `radius`, to be stroked (open polyline, round caps) rather than filled.
 */
export function buildHandleIconPath(
  icon: HandleIcon,
  center: Point2D,
  radius: number,
): Point2D[] {
  switch (icon) {
    case "move":
      return buildMoveIcon(center, radius);
  }
}

/**
 * The four-way "move" icon, like Phosphor's arrows-out-cardinal: a cross whose
 * arms end in a chevron. Drawn as ONE stroke that goes back over its own
 * segments (center → tip → wing → tip → wing → tip → center, for each arm):
 * retraced segments are invisible, and the icon stays a single overlay.
 */
function buildMoveIcon(center: Point2D, radius: number): Point2D[] {
  const arm = radius * 0.56;
  const wing = radius * 0.14;

  // Arm pointing up (negative y) in local coordinates.
  const tip = { x: 0, y: -arm };
  const stroke: Point2D[] = [
    { x: 0, y: 0 },
    tip,
    { x: -wing, y: -arm + wing },
    tip,
    { x: wing, y: -arm + wing },
    tip,
  ];

  const points: Point2D[] = [];
  // Quarter turns clockwise on screen (y pointing down): (x, y) → (-y, x).
  for (let turn = 0; turn < 4; turn++) {
    for (const point of stroke) {
      let { x, y } = point;
      for (let i = 0; i < turn; i++) [x, y] = [-y, x];
      points.push({ x: center.x + x, y: center.y + y });
    }
  }
  points.push({ ...center });
  return points;
}
